import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Check, Pencil, Send, XCircle } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/features/clients/constants";
import { useCreditNote, useUpdateCreditNoteStatus } from "@/features/credit-notes/api";
import {
  CN_STATUSES,
  canEditLines,
  labelFor as cnLabelFor,
  statusTone,
} from "@/features/credit-notes/constants";
import type { CreditNoteStatus } from "@/features/credit-notes/types";

export function CreditNoteDetailPage({ cnId }: { cnId: string }) {
  return (
    <ErrorBoundary title="Credit note detail crashed">
      <CreditNoteDetailInner cnId={cnId} />
    </ErrorBoundary>
  );
}

function CreditNoteDetailInner({ cnId }: { cnId: string }) {
  const navigate = useNavigate();
  const { data: cn, isLoading, error } = useCreditNote(cnId);
  const updateStatus = useUpdateCreditNoteStatus();

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (error || !cn) {
    return (
      <EmptyState
        title="Credit note not found"
        description={(error as Error)?.message}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/credit-notes">Back</Link>
          </Button>
        }
      />
    );
  }

  async function setStatus(status: CreditNoteStatus) {
    await updateStatus.mutateAsync({ id: cnId, status });
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Button asChild variant="ghost" size="icon-sm">
            <Link to="/credit-notes">
              <ArrowLeft />
            </Link>
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-mono text-lg font-semibold">{cn.cn_number}</h1>
              <StatusDot tone={statusTone(cn.status)}>
                {cnLabelFor(CN_STATUSES, cn.status)}
              </StatusDot>
            </div>
            <p className="text-xs text-muted-foreground">
              {cn.client_code} · {cn.client_name} · {format(parseISO(cn.issue_date), "dd MMM yyyy")}
              {cn.invoice_number ? ` · ${cn.invoice_number}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {canEditLines(cn.status) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: "/credit-notes/$cnId/edit", params: { cnId } })}
            >
              <Pencil />
              Edit
            </Button>
          ) : null}
          {cn.status === "draft" ? (
            <Button
              type="button"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("issued")}
            >
              <Send />
              Issue (credit AR)
            </Button>
          ) : null}
          {["draft", "issued"].includes(cn.status) ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("cancelled")}
            >
              <XCircle />
              Cancel
            </Button>
          ) : null}
        </div>
      </header>

      {cn.status === "issued" ? (
        <p className="rounded border border-dashed px-3 py-2 text-xs text-muted-foreground">
          <Check className="mr-1 inline size-3.5" />
          AR reduced by {formatMoney(cn.grand_total)}
        </p>
      ) : null}

      {(cn.reason || cn.invoice_id) && (
        <div className="grid gap-3 text-xs md:grid-cols-2">
          {cn.reason ? (
            <section className="rounded border p-3">
              <h2 className="mb-1 font-medium">Reason</h2>
              <p className="text-muted-foreground">{cn.reason}</p>
            </section>
          ) : null}
          {cn.invoice_id && cn.invoice_number ? (
            <section className="rounded border p-3">
              <h2 className="mb-1 font-medium">Against invoice</h2>
              <Link
                to="/invoices/$invId"
                params={{ invId: cn.invoice_id }}
                className="font-mono hover:underline"
              >
                {cn.invoice_number}
              </Link>
            </section>
          ) : null}
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Material</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Wt</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(cn.lines ?? []).map((line) => (
              <TableRow key={line.id} className="text-xs">
                <TableCell>{line.line_no}</TableCell>
                <TableCell>
                  {line.material_name}
                  {line.description ? (
                    <span className="block text-2xs text-muted-foreground">{line.description}</span>
                  ) : null}
                </TableCell>
                <TableCell className="text-right tabular-nums">{line.qty || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.weight > 0 ? line.weight : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">{line.unit_rate}</TableCell>
                <TableCell className="text-right tabular-nums">{line.gst_slab}%</TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {formatMoney(line.line_subtotal)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <footer className="flex justify-end text-xs">
        <div className="space-y-1 text-right">
          <div>
            Tax <strong className="tabular-nums">{formatMoney(cn.tax_total)}</strong>
          </div>
          <div className="text-sm">
            Credit total{" "}
            <strong className="tabular-nums text-primary">{formatMoney(cn.grand_total)}</strong>
          </div>
        </div>
      </footer>
    </div>
  );
}
